import {
  IClient,
  IClientACPM,
  IClientACPMData,
  IClientData,
  IClientGeneralInfo,
  IClientGeneralInfoData,
  IClientInstruments,
  IClientInstrumentsData,
  IClientPageLimits,
  IClientPageLimitsData,
  IClientPaymentMethod,
  IClientPriorityRuleset,
  IClientPriorityRulesetData,
  IClientRoutingPaymentInstrument,
  IClientRoutingPriority,
  IClientRoutingPriorityData,
  IClientRoutingStats,
  IClientSavedCardToken,
  IClientsLimits,
  IClientStats,
  IClientTopErrors,
  IClientTopErrorsData,
  ICustomParameter,
  IPaymentInstrument,
  IPaymentMethod,
} from 'api/clients/types';
import { INullable, TObject } from 'utils/types';

import { TRANSACTIONS_TYPE } from '../transactions/constants';

export const normalizeClient = (data: IClientData): IClient => {
  return {
    addressLine1: (data?.addressLine1 as string) || '',
    addressLine2: (data?.addressLine2 as string) || '',
    city: (data?.city as string) || '',
    countryCode: (data?.countryCode as string) || '',
    email: (data?.email as string) || '',
    firstName: (data?.firstName as string) || '',
    firstTimeDepositDate: (data?.firstTimeDepositDate as string) || '',
    gender: (data?.gender as string) || '',
    id: (data?.id as number) || 0,
    ip: (data?.ip as string) || '',
    lastName: (data?.lastName as string) || '',
    merchantCustomerId: (data?.merchantCustomerId as string) || '',
    merchantTerminal: {
      id: (data?.merchantTerminal?.id as number) || 0,
      merchant: {
        id: (data?.merchantTerminal?.merchant?.id as number) || 0,
        name: (data?.merchantTerminal?.merchant?.name as string) || '',
      },
      name: (data?.merchantTerminal?.name as string) || '',
    },
    personalId: (data?.personalId as string) || '',
    phone: (data?.phone as string) || '',
    phoneCountryCode: (data?.phoneCountryCode as string) || '',
    postalCode: (data?.postalCode as string) || '',
    registrationCountryCode: (data?.registrationCountryCode as string) || '',
    registrationDate: (data?.registrationDate as string) || '',
    registrationIp: (data?.registrationIp as string) || '',
    secondDepositDate: (data?.secondDepositDate as string) || '',
    taxNumber: (data?.taxNumber as string) || '',
  };
};

const normalizeClientCustomParams = (data: TObject): INullable<ICustomParameter[]> => {
  return (
    Object.entries(data || {}).map(([key, value]) => ({
      key,
      value: value as string,
    })) || []
  );
};

export const normalizeClientGeneralInfo = (
  data: IClientGeneralInfoData,
): IClientGeneralInfo => {
  return {
    addressLine1: (data?.addressLine1 as string) || '',
    addressLine2: (data?.addressLine2 as string) || '',
    city: (data?.city as string) || '',
    countryCode: (data?.countryCode as string) || '',
    customParams: normalizeClientCustomParams(data?.customParams),
    dateOfBirth: (data?.dateOfBirth as string) || '',
    email: (data?.email as string) || '',
    firstName: (data?.firstName as string) || '',
    gender: (data?.gender as string) || null,
    id: (data?.id as number) || 0,
    ip: (data?.ip as string) || '',
    lastName: (data?.lastName as string) || '',
    locale: (data?.locale as string) || null,
    merchantCustomerId: (data?.merchantCustomerId as string) || '',
    merchantTerminalId: (data?.merchantTerminalId as number) || 0,
    merchantTerminalName: (data?.merchantTerminalName as string) || '',
    personalId: (data?.personalId as string) || null,
    phone: (data?.phone as string) || '',
    phoneCountryCode: (data?.phoneCountryCode as string) || '',
    postalCode: (data?.postalCode as string) || '',
    registrationDate: (data?.registrationDate as string) || '',
    registrationIp: (data?.registrationIp as string) || null,
    state: (data?.state as string) || '',
    taxNumber: (data?.taxNumber as string) || null,
    terminalLinkDescription: (data?.terminalLinkDescription as string) || '',
    terminalLinkId: (data?.terminalLinkId as number) || 0,
  };
};

export const normalizeClientStats = (data: TObject): IClientStats => {
  return {
    baseCurrency: (data?.baseCurrency as string) || '',
    depositAmount: data?.depositAmount as number,
    depositAverageAmount: data?.depositAverageAmount as number,
    depositCount: data?.depositCount as number,
    deposits: (data?.deposits as IClientStats['deposits']) || {},
    firstDepositTry: (data?.firstDepositTry as string) || '',
    firstTimeDeposit: (data?.firstTimeDeposit as string) || '',
    firstTimeDepositId: (data?.firstTimeDepositId as string) || '',
    id: (data?.id as number) || 0,
    secondDeposit: (data?.secondDeposit as string) || '',
    secondDepositId: (data?.secondDepositId as string) || '',
    successDepositPercentage: data?.successDepositPercentage as number,
    withdrawals: (data?.withdrawals as IClientStats['withdrawals']) || {},
    withdrawAmount: data?.withdrawAmount as number,
    withdrawAverageAmount: data?.withdrawAverageAmount as number,
    withdrawCount: data?.withdrawCount as number,
  };
};

export const normalizeClientErrorsData = (data: TObject): IClientTopErrorsData => {
  return {
    count: data?.count as number,
    errorCode: (data?.errorCode as string) || '',
    errorMessage: (data?.errorMessage as string) || '',
  };
};

type IClientErrorsData = TObject & {
  listCustomerMethodErrorStats: TObject[];
};

export const normalizeClientErrors = (data: IClientErrorsData): IClientTopErrors => {
  return {
    id: (data?.id as number) || 0,
    listCustomerMethodErrorStats:
      data?.listCustomerMethodErrorStats?.map(normalizeClientErrorsData) || [],
  };
};

export const normalizePaymentMethodData = (data: TObject): IPaymentMethod => {
  return {
    authRate: data?.authRate as number,
    bankName: (data?.bankName as string) || '',
    cardHolder: (data?.cardHolder as string) || '',
    cardMask: (data?.cardMask as string) || '',
    depositAmount: data?.depositAmount as number,
    depositCount: data?.depositCount as number,
    methodName: (data?.methodName as string) || '',
  };
};

type IPaymentMethodData = TObject & {
  listCustomerMethodStats: TObject[];
};

export const normalizePaymentMethod = (
  data: IPaymentMethodData,
): IClientPaymentMethod => {
  return {
    baseCurrency: (data?.baseCurrency as string) || '',
    id: (data?.id as number) || 0,
    listCustomerMethodStats:
      data?.listCustomerMethodStats?.map(normalizePaymentMethodData) || [],
  };
};

export const normalizeClientPageLimit = (
  data: IClientPageLimitsData,
): IClientPageLimits => {
  return {
    currency: (data?.currency as string) || '',
    id: (data?.id as number) || null,
    maxAmount: (data?.maxAmount as number) || null,
    minAmount: (data?.minAmount as number) || null,
    name: (data?.name as string) || '',
    operation: (data?.operation as string) || '',
    paymentMethod: (data?.paymentMethod as string) || '',
  };
};

export const normalizeClientAllowedPM = (data: IClientACPMData): IClientACPM => {
  return {
    id: (data?.id as number) || 0,
    paymentMethod: {
      name: (data?.paymentMethod?.name as string) || '',
      paymentMethod: (data?.paymentMethod?.paymentMethod as string) || '',
    },
  };
};

export const normalizeClientPriorityRulesets = (
  data: IClientPriorityRulesetData,
): IClientPriorityRuleset => {
  return {
    id: (data?.id as number) || 0,
    paymentMethod: (data?.paymentMethod as string) || '',
    paymentMethodId: (data?.paymentMethodId as string) || '',
    routingSchemaName: (data?.routingSchemaName as string) || '',
    ruleset: {
      id: (data?.ruleset?.id as number) || null,
      name: (data?.ruleset?.name as string) || '',
    },
  };
};

// Nested under routingRuleset so grid dataKeys match the BE sort and filter keys
export const normalizeClientRoutingPriority = (
  data: IClientRoutingPriorityData,
): IClientRoutingPriority => ({
  id: (data?.id as number) || 0,
  priority: (data?.priority as number) ?? 0,
  routingRuleset: {
    cashierPaymentMethod: {
      name: (data?.cashierPaymentMethod?.name as string) || '',
      paymentMethod: (data?.cashierPaymentMethod?.paymentMethod as string) || '',
    },
    enabled: !!data?.ruleset?.enabled,
    id: (data?.ruleset?.id as number) || 0,
    name: (data?.ruleset?.name as string) || '',
    priority: (data?.ruleset?.priority as number) ?? null,
    ratio: (data?.ruleset?.ratio as number) ?? null,
    routingSchema: {
      name: (data?.routingSchemaName as string) || '',
    },
    terminalLink: {
      description: (data?.terminalLinkDescription as string) || '',
    },
  },
});

export const normalizeClientsLimits = (data: TObject): IClientsLimits => {
  return {
    actualDailyVolume: (data?.actualDailyVolume as number) || 0,
    actualMonthlyVolume: (data?.actualMonthlyVolume as number) || 0,
    actualWeeklyVolume: (data?.actualWeeklyVolume as number) || 0,
    dailyLimit: (data?.dailyLimit as number) || null,
    id: (data?.id as number) || null,
    monthlyLimit: (data?.monthlyLimit as number) || null,
    weeklyLimit: (data?.weeklyLimit as number) || null,
  };
};

export const normalizeUniquePaymentInstruments = (
  data: TObject[],
): IPaymentInstrument[] => {
  return data?.map((item) => ({
    id: (item.id as number) || null,
    maskedCardPan: (item.maskedCardPan as string) || null,
    newCardNotificationSent: !!item.newCardNotificationSent,
    paymentMethod: (item.paymentMethod as string) || null,
    payoutAllowed: !!item.payoutAllowed,
    transactionType: (item.transactionType as string) || null,
  }));
};

export const normalizeClientRoutingStats = (data: TObject): IClientRoutingStats => {
  return {
    cardDepositAmount: (data?.cardDepositAmount as number) || 0,
    cardDepositCount: (data?.cardDepositCount as number) || 0,
    cardWithdrawalAmount: (data?.cardWithdrawalAmount as number) || 0,
    cardWithdrawalCount: (data?.cardWithdrawalCount as number) || 0,
    depositAmount: (data?.depositAmount as number) || 0,
    depositCount: (data?.depositCount as number) || 0,
    uniqueCardDepositAmount: (data?.uniqueCardDepositAmount as number) || 0,
    uniqueCardDepositCount: (data?.uniqueCardDepositCount as number) || 0,
    uniqueCardWithdrawalAmount: (data?.uniqueCardWithdrawalAmount as number) || 0,
    uniqueCardWithdrawalCount: (data?.uniqueCardWithdrawalCount as number) || 0,
    withdrawalAmount: (data?.withdrawalAmount as number) || 0,
    withdrawalCount: (data?.withdrawalCount as number) || 0,
  };
};

export const normalizeClientRoutingPaymentInstruments = (
  data: TObject,
): IClientRoutingPaymentInstrument => {
  return {
    created: (data?.created as string) || null,
    id: (data?.id as number) || null,
    maskedCardPan: (data?.maskedCardPan as string) || null,
    modified: (data?.modified as string) || null,
    txAmount: (data?.txAmount as number) || null,
    txCount: (data?.txCount as number) || null,
    txType: (data?.txType as TRANSACTIONS_TYPE) || null,
  };
};

export const normalizeClientSavedCardToken = (data: TObject): IClientSavedCardToken => {
  return {
    binCountryName: (data?.binCountryName as string) || null,
    binLength: (data?.binLength as number) || null,
    cardBrand: (data?.cardBrand as string) || null,
    cardCategory: (data?.cardCategory as string) || null,
    cardCountry: (data?.cardCountry as string) || null,
    cardExpiryDate: (data?.cardExpiryDate as string) || null,
    cardFullBin: (data?.cardFullBin as string) || null,
    cardholder: (data?.cardholder as string) || null,
    cardIssuingOrganization: (data?.cardIssuingOrganization as string) || null,
    cardPanId: (data?.cardPanId as string) || null,
    cardType: (data?.cardType as string) || null,
    maskedCardPan: (data?.maskedCardPan as string) || null,
    verification: (data?.verification as string) || null,
  };
};

export const normalizeClientInstruments = (
  data: IClientInstrumentsData,
): IClientInstruments => {
  return {
    binCountryName: (data?.binCountryName as string) || null,
    binLength: data?.binLength as null | number,
    blockedByCardMask: !!data?.blockedByCardMask,
    blockedByPan: !!data?.blockedByPan,
    cardBrand: (data?.cardBrand as string) || null,
    cardCategory: (data?.cardCategory as string) || null,
    cardCountry: (data?.cardCountry as string) || null,
    cardExpiryDate: (data?.cardExpiryDate as string) || null,
    cardFullBin: (data?.cardFullBin as string) || null,
    cardholder: (data?.cardholder as string) || null,
    cardIssuingOrganization: (data?.cardIssuingOrganization as string) || null,
    cardPanId: (data?.cardPanId as number) || null,
    cardType: (data?.cardType as string) || null,
    customer: {
      email: (data?.customer?.email as string) || null,
      id: (data?.customer?.id as number) || 0,
      merchantCustomerId: (data?.customer?.merchantCustomerId as string) || '',
    },
    maskedCardPan: (data?.maskedCardPan as string) || null,
    merchantTerminal: {
      id: (data?.merchantTerminal?.id as number) || 0,
      merchant: {
        enabled: !!data?.merchantTerminal?.merchant?.enabled,
        id: (data?.merchantTerminal?.merchant?.id as number) || 0,
        name: (data?.merchantTerminal?.merchant?.name as string) || '',
      },
      name: (data?.merchantTerminal?.name as string) || '',
      preMemorisedRouting: !!data?.merchantTerminal?.preMemorisedRouting,
    },
    verification: (data?.verification as string) || null,
  };
};
